import React, { useEffect, useState } from "react";
import { useTeamsQuery } from "../../../Services/settingsAPIs/CommonAPI";
import {
  useDespositionDataQuery,
  useSaveDespositionMutation,
} from "../../../Services/general/General";
import { useTicketMapToDispositionMutation } from "../../../Services/settingsAPIs/TicketSettingsAPI";
import { toast } from "react-toastify";
import LoadingSpinner from "../../../components/LoadingSpinner";
import { capitalizeFirstLetter } from "../../../components/reusableComponent/CapitalLetter";
import { Loader } from "react-bootstrap-typeahead";

type Card = {
  id: number;
  label: string;
  description?: string;
  steps?: string[];
  priority: string;
  sub_topics: Card[];
  level?: string;
};

const PrioritySettings = () => {
  const [selectedTeam, setSelectedTeam] = useState<string>("");
  const [cardsData, setCardsData] = useState<Card[]>([]);
  const [priorityTeamsData, setPriorityTeamsData] = useState<any[]>([]);
  const [storedData, setStoredData] = useState<Card[]>([]);
  const [userRole, setUserRole] = useState<string>("");
  const [expandedNodes, setExpandedNodes] = useState<string[]>([]);
  const { data: teamsfetched, isLoading: teamsLoading } = useTeamsQuery({});
  const [updateDespositionTrigger] = useTicketMapToDispositionMutation();
  const [storingPirorityData, setStoringPirorityData] = useState<Card[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [trigger] = useSaveDespositionMutation();
  const {
    data,
    refetch,
    isLoading: priorityLoading,
  } = useDespositionDataQuery(selectedTeam, {
    skip: !selectedTeam,
  });

  const fetchUserRole = () => {
    const currentUser = { role: "admin" };
    setUserRole(currentUser.role);
  };

  useEffect(() => {
    fetchUserRole();
  }, []);

  useEffect(() => {
    if (userRole === "admin" && teamsfetched) {
      setPriorityTeamsData(teamsfetched);
    } else if (userRole !== "admin" && userRole) {
      const userTeams = [
        { id: 1, name: "Team A" },
        { id: 2, name: "Team B" },
      ];
      setPriorityTeamsData(userTeams);
    }
  }, [userRole, teamsfetched]);

  useEffect(() => {
    if (data) {
      setStoredData(data);
      const extractedCards = renderCardData(data);
      const aligned = alignFormat(extractedCards);
      setStoringPirorityData(aligned);
      setCardsData(extractedCards);
      setHasChanges(false); // Reset change tracking
    }
  }, [data]);

  function alignFormat(arr: Card[]) {
    function addLevels(subTopics: Card[], levelPrefix: string) {
      return subTopics.map((item, index) => {
        let newItem = { ...item, level: `${levelPrefix}${index}` };
        if (newItem.sub_topics?.length) {
          newItem.sub_topics = addLevels(
            newItem.sub_topics,
            `${newItem.level}:`
          );
        }
        return newItem;
      });
    }

    return arr.map((item, index) => {
      let newItem = { ...item, level: `${index}` };
      if (newItem.sub_topics?.length) {
        newItem.sub_topics = addLevels(newItem.sub_topics, `${index}:`);
      }
      return newItem;
    });
  }

  const removeLevels = (arr: Card[]) => {
    return arr.map((item) => {
      let newItem = { ...item };
      delete newItem.level;
      if (newItem.sub_topics?.length) {
        newItem.sub_topics = removeLevels(newItem.sub_topics);
      }
      return newItem;
    });
  };

  const updatePriority = (original: Card[], updated: Card[]): Card[] => {
    return original.map((item) => {
      const updatedItem = updated.find((u) => u.id === item.id);
      const newPriority = updatedItem?.priority || item.priority;
      const updatedSubTopics = item.sub_topics?.length
        ? updatePriority(item.sub_topics, updatedItem?.sub_topics || [])
        : [];
      return {
        ...item,
        priority: newPriority,
        sub_topics: updatedSubTopics,
      };
    });
  };

  const saveHandler = async () => {
    setIsSaving(true);
    const updatedData = updatePriority(storedData, storingPirorityData);

    try {
      await updateDespositionTrigger({
        body: updatedData,
        teamId: selectedTeam,
      }).unwrap();
      toast.success("Edited successfully");
      refetch();
      setHasChanges(false);
    } catch (error) {
      toast.error("Failed to update");
    } finally {
      setIsSaving(false);
    }
  };

  const handleFirstDropdownChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setSelectedTeam(event.target.value);
  };

  const handlePriorityChange = (
    id: number,
    newPriority: string,
    cards: Card[]
  ): Card[] => {
    return cards.map((card) =>
      card.id === id
        ? { ...card, priority: newPriority }
        : {
            ...card,
            sub_topics: handlePriorityChange(id, newPriority, card.sub_topics),
          }
    );
  };

  const handlePriorityChangeWrapper = (
    id: number,
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const newPriority = event.target.value;
    setStoringPirorityData((prev) => {
      const updated = handlePriorityChange(id, newPriority, prev);
      const cleanedStored = JSON.stringify(removeLevels(storedData));
      const cleanedUpdated = JSON.stringify(removeLevels(updated));
      setHasChanges(cleanedStored !== cleanedUpdated);
      return updated;
    });
  };

  const renderCardData = (data: any[]): Card[] => {
    const extractCards = (data: any[]): Card[] => {
      return data.map((item) => ({
        id: item.id,
        label: item.topic || "",
        description: item.description || "",
        steps: item.steps || [],
        priority: item.priority || "Low",
        sub_topics: item.sub_topics ? extractCards(item.sub_topics) : [],
      }));
    };
    return extractCards(data);
  };

  const toggleNode = (level: string) => {
    setExpandedNodes((prev) =>
      prev.includes(level) ? prev.filter((l) => l !== level) : [...prev, level]
    );
  };

  const renderTreeNode = (node: Card) => {
    const isLeafNode = !node.sub_topics || node.sub_topics.length === 0;

    return (
      <div key={node.level} className="ms-8 mb-2">
        <div className="d-flex align-items-center justify-content-between">
          <div
            className="d-flex align-items-center"
            style={{ cursor: "pointer" }}
            onClick={() => toggleNode(node?.level!)}
          >
            <i
              className={`bi ${
                node.sub_topics?.length
                  ? "bi-chevron-" +
                    (expandedNodes.includes(node?.level!) ? "down" : "right")
                  : "bi-dash"
              } me-2`}
            ></i>
            <span>{capitalizeFirstLetter(node?.label)}</span>
          </div>

          {isLeafNode && (
            <div style={{ minWidth: "130px" }}>
              <select
                value={node.priority}
                onChange={(e) => handlePriorityChangeWrapper(node.id, e)}
                className="form-select form-select-sm"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          )}
        </div>

        {expandedNodes.includes(node.level!) && node.sub_topics?.length > 0 && (
          <div className="ms-4 border-start ps-2 mt-2">
            {node.sub_topics.map((subNode) => renderTreeNode(subNode))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      className="card-toolbar flex-row-fluid justify-content-end gap-5 p-5"
      style={{ marginLeft: "2%" }}
    >
      {teamsLoading || priorityLoading ? (
        <LoadingSpinner />
      ) : (
        <>
          {/* Team Selector */}
          <div className="row mb-4">
            <div className="col-12 mw-200px">
              <label>Select team</label>
              <select
                className="form-select select2-hidden-accessible"
                onChange={handleFirstDropdownChange}
                value={selectedTeam}
              >
                <option disabled value="">
                  Select team
                </option>
                {priorityTeamsData?.map((team) => (
                  <option key={team.id} value={team.id}>
                    {team.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Cards Display */}
          <div className="mt-5">
            <div style={{ overflowY: "auto", maxHeight: "62vh" }}>
              {storingPirorityData?.map((tree, index) => (
                <div key={index} className="card p-3 fs-5 mb-2">
                  {renderTreeNode(tree)}
                </div>
              ))}
            </div>

            {/* Save button */}
            {selectedTeam && (
              <div className="text-end mt-5">
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  onClick={saveHandler}
                  disabled={!hasChanges || isSaving}
                >
                  {isSaving ? (
                    <>
                      Saving... <Loader />
                    </>
                  ) : (
                    "Save"
                  )}
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default PrioritySettings;